'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type PropsWithChildren, useContext, useState } from 'react';
import type { TimePickerMode, TimePickerValue, TimeType } from './types';
type Context = {
  disabled: boolean;
  invalid: boolean;
  mode: TimePickerMode;
  reset: () => void;
  setValue: (value: TimePickerValue) => void;
  size: 'default' | 'large';
  value: TimePickerValue;
};
const TimePickerContext = createContext<Context | null>(null);
const collectionDefaults: Record<TimeType, TimePickerValue> = {
  '24': {
    from: '08:00',
    to: '16:00',
    type: 'between'
  },
  asap: {
    type: 'asap'
  },
  at: {
    from: '08:00',
    type: 'at'
  },
  between: {
    from: '08:00',
    to: '16:00',
    type: 'between'
  },
  by: {
    to: '16:00',
    type: 'by'
  },
  tbc: {
    type: 'tbc'
  }
};
const deliveryDefaults: Record<TimeType, TimePickerValue> = {
  '24': {
    from: '08:00',
    to: '16:00',
    type: 'between'
  },
  asap: {
    type: 'asap'
  },
  at: {
    type: 'at'
  },
  between: {
    from: '08:00',
    to: '16:00',
    type: 'between'
  },
  by: {
    to: '16:00',
    type: 'by'
  },
  tbc: {
    type: 'tbc'
  }
};
const schemaDefaults: Record<TimeType, TimePickerValue> = {
  '24': {
    from: '08:00',
    to: '16:00',
    type: '24'
  },
  asap: {
    type: 'asap'
  },
  at: {
    from: '08:00',
    type: 'at'
  },
  between: {
    from: '08:00',
    to: '16:00',
    type: 'between'
  },
  by: {
    to: '16:00',
    type: 'by'
  },
  tbc: {
    type: 'tbc'
  }
};
export const getDefaultValue = (mode: TimePickerMode): TimePickerValue => {
  if (mode === 'collection') return collectionDefaults.between;
  if (mode === 'delivery') return deliveryDefaults.asap;
  return schemaDefaults.between;
};
export const getDefaultForType = (mode: TimePickerMode, type: TimeType): TimePickerValue => {
  const defaults = mode === 'collection' ? collectionDefaults : mode === 'delivery' ? deliveryDefaults : schemaDefaults;
  return defaults[type];
};
type TimePickerProviderProps = PropsWithChildren<{
  defaultValue?: TimePickerValue;
  disabled?: boolean;
  invalid?: boolean;
  mode: TimePickerMode;
  onChange?: (value: TimePickerValue) => void;
  size?: 'default' | 'large';
  value?: TimePickerValue;
}>;
export const TimePickerProvider = t0 => {
  const $ = _c(20);
  const {
    children,
    defaultValue: initialValue,
    disabled: t1,
    invalid: t2,
    mode,
    onChange,
    size: t3,
    value: controlledValue
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const invalid = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "default" : t3;
  let t4;
  if ($[0] !== initialValue || $[1] !== mode) {
    t4 = initialValue ?? getDefaultValue(mode);
    $[0] = initialValue;
    $[1] = mode;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const defaultVal = t4;
  const [internalValue, setInternalValue] = useState(defaultVal);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  let t5;
  if ($[3] !== isControlled || $[4] !== onChange) {
    t5 = newValue => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    };
    $[3] = isControlled;
    $[4] = onChange;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const setValue = t5;
  let t6;
  if ($[6] !== defaultVal || $[7] !== setValue) {
    t6 = () => setValue(defaultVal);
    $[6] = defaultVal;
    $[7] = setValue;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const reset = t6;
  let t7;
  if ($[9] !== disabled || $[10] !== invalid || $[11] !== mode || $[12] !== reset || $[13] !== setValue || $[14] !== size || $[15] !== value) {
    t7 = {
      disabled,
      invalid,
      mode,
      reset,
      setValue,
      size,
      value
    };
    $[9] = disabled;
    $[10] = invalid;
    $[11] = mode;
    $[12] = reset;
    $[13] = setValue;
    $[14] = size;
    $[15] = value;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== children || $[18] !== t7) {
    t8 = <TimePickerContext value={t7} data-sentry-element="TimePickerContext" data-sentry-component="TimePickerProvider" data-sentry-source-file="provider.tsx">{children}</TimePickerContext>;
    $[17] = children;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
export const useTimePicker = () => {
  const context = useContext(TimePickerContext);
  if (!context) {
    throw new Error("useTimePicker must be used within TimePickerProvider");
  }
  return context;
};